/**
 * CandidateSignalsPage.js — RC 1.30.H.
 *
 * Second half of the employer↔candidate consent handshake. The
 * candidate sees a calm list of organisations that have expressed
 * interest in their anonymous profile, with three response
 * options: Accept, Decline, Defer.
 *
 * Design tenets
 * ─────────────
 *   • Candidate control tone. No urgency. No "message" or
 *     "contacted" language — an employer has "expressed interest",
 *     full stop.
 *   • No identity is revealed in either direction in this slice.
 *     Accepting does NOT yet surface the candidate's PII to the
 *     employer; that reciprocity gate ships in RC 1.30.I.
 *   • Responses are immediate — no confirmation modal, no undo,
 *     no toast fatigue.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card, CardContent,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertCircle, Building2, Check, ChevronDown, Clock, ExternalLink,
  Inbox, Loader2, MapPin, PauseCircle, X,
} from 'lucide-react';
import { toast } from 'sonner';

import { apiFetch } from '@/lib/api';
import {
  formatRelativeTime,
} from '@/pages/employerMatchesHelpers';


const STATUS_PILL = {
  pending:   'state-pill state-pill--pending',
  accepted:  'state-pill state-pill--accepted',
  declined:  'state-pill state-pill--declined',
  deferred:  'state-pill state-pill--deferred',
};

const STATUS_LABEL = {
  pending:  'Your decision pending',
  accepted: 'Accepted',
  declined: 'Declined',
  deferred: 'Deferred',
};

const STATUS_RAIL = {
  pending:  'state-rail--pending',
  accepted: 'state-rail--accepted',
  declined: 'state-rail--declined',
  deferred: 'state-rail--deferred',
};


// ─────────────────────────────────────────────────────────────
// Signal card
// ─────────────────────────────────────────────────────────────

export function SignalCard({ signal, onRespond, pending, pendingAction }) {
  const isPending = signal.status === 'pending';
  const isTerminal = signal.status === 'declined' || signal.status === 'deferred';
  const role = signal.role || {};
  const org  = signal.organisation || {};
  const pillClass = STATUS_PILL[signal.status] || STATUS_PILL.pending;
  const railClass = STATUS_RAIL[signal.status] || '';

  // RC 1.30.I follow-up — Issue 1: render org credibility meta
  // only when at least one non-name field is present. Null/empty
  // fields are skipped row-by-row so the panel is never empty.
  const hasOrgMeta = Boolean(
    org.sector || org.size_band || org.description || org.website,
  );

  // P1.2 + P1.3 — consistent expand/collapse.
  //   pending  → default EXPANDED (the user needs to decide)
  //   accepted → default COLLAPSED (access block is the headline;
  //              secondary sections are optional context)
  //   terminal → default COLLAPSED (historical, low priority)
  const [expanded, setExpanded] = useState(signal.status === 'pending');

  return (
    <Card
      className={`border-ndor-edge shadow-sm transition hover:border-ndor-ink-muted ${railClass} ${isTerminal ? 'opacity-80' : ''}`}
      data-testid={`signal-card-${signal.id}`}
    >
      <CardContent className="p-5 space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 text-xs text-ndor-ink-muted">
              <Building2 className="h-3.5 w-3.5" />
              <span
                data-testid={`signal-org-${signal.id}`}
                className="font-medium text-ndor-ink"
              >
                {role.organisation_name || 'An organisation'}
              </span>
              <span>has expressed interest</span>
            </div>
            <h3
              className="mt-1 text-base font-semibold text-ndor-ink"
              data-testid={`signal-role-${signal.id}`}
            >
              {role.title || 'Role'}
            </h3>
            <div className="mt-1 flex items-center gap-3 text-[11px] text-ndor-ink-muted">
              {role.location && (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="h-3 w-3" /> {role.location}
                </span>
              )}
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" /> {formatRelativeTime(signal.sent_at)}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span
              className={pillClass}
              data-testid={`signal-status-${signal.id}`}
            >
              {STATUS_LABEL[signal.status] || signal.status}
            </span>
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="rounded-md p-1.5 text-ndor-ink-muted hover:bg-ndor-surface-alt hover:text-ndor-ink transition-colors"
              data-testid={`signal-toggle-${signal.id}`}
              aria-expanded={expanded}
              aria-label={expanded ? 'Collapse signal details' : 'Expand signal details'}
            >
              <ChevronDown
                className={`h-4 w-4 transition-transform duration-150 ${
                  expanded ? 'rotate-180' : ''
                }`}
              />
            </button>
          </div>
        </div>

        {/* RC 1.30.I follow-up — Issue 1: organisation credibility */}
        {expanded && hasOrgMeta && (
          <div
            className="rounded-md border border-ndor-edge bg-ndor-surface-alt px-3 py-2 space-y-1.5"
            data-testid={`signal-org-meta-${signal.id}`}
          >
            <div className="text-[10px] uppercase tracking-wider text-ndor-ink-muted">
              About this organisation
            </div>
            {(org.sector || org.size_band) && (
              <div
                className="text-xs text-ndor-ink"
                data-testid={`signal-org-meta-sector-${signal.id}`}
              >
                {[org.sector, org.size_band].filter(Boolean).join(' · ')}
              </div>
            )}
            {org.description && (
              <p
                className="text-xs text-ndor-ink-muted leading-relaxed"
                data-testid={`signal-org-meta-description-${signal.id}`}
              >
                {org.description.length > 280
                  ? `${org.description.slice(0, 279).trimEnd()}…`
                  : org.description}
              </p>
            )}
            {org.website && (
              <a
                href={org.website}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[11px] font-medium text-ndor-ink underline decoration-dotted underline-offset-2 hover:decoration-solid"
                data-testid={`signal-org-meta-website-${signal.id}`}
              >
                Website <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        )}

        {/* RC 1.30.I — Access granted banner + reciprocity.
            P0 copy refresh: explicit scope, permanence, and
            support routing. No withdraw affordance.
            RC 1.62 Channel-2 lifecycle UX: collapsed by default
            on terminal states. The status pill in the header
            communicates "Accepted" succinctly; the full block
            (including reciprocity) only renders when the
            candidate expands the card. */}
        {expanded && signal.status === 'accepted' && signal.access_status && (
          <div
            className="rounded-md border px-3 py-2.5 space-y-2.5"
            style={{
              backgroundColor: 'hsl(var(--ndor-state-accepted-bg))',
              borderColor: 'hsl(var(--ndor-state-accepted-border))',
            }}
            data-testid={`signal-access-${signal.id}`}
          >
            <div
              className="flex items-center gap-1.5 text-xs font-semibold"
              style={{ color: 'hsl(var(--ndor-state-accepted-text))' }}
            >
              <Check className="h-3.5 w-3.5" /> Access granted
            </div>
            <p className="text-[11px] text-ndor-ink leading-relaxed">
              Your profile is accessible to this organisation for this
              role only. This access was granted when you accepted and
              is scoped to this match.
            </p>
            <p
              className="text-[11px] text-ndor-ink-secondary leading-relaxed"
              data-testid={`signal-access-permanence-${signal.id}`}
            >
              To close this access, contact support.
            </p>
            <p className="text-[11px] text-ndor-ink-muted leading-relaxed italic">
              Access does not extend to other roles or signals from this
              organisation.
            </p>

            {signal.reciprocity?.reciprocity_summary && (
              <div
                className="pt-2 border-t"
                style={{ borderColor: 'hsl(var(--ndor-state-accepted-border))' }}
              >
                <div className="text-[10px] uppercase tracking-wider text-ndor-ink-muted mb-1">
                  What this organisation knows about you
                </div>
                <p
                  className="text-[11px] text-ndor-ink leading-relaxed"
                  data-testid={`signal-reciprocity-summary-${signal.id}`}
                >
                  {signal.reciprocity.reciprocity_summary}
                </p>
              </div>
            )}
            {signal.reciprocity?.candidate_benefit_points?.length > 0 && expanded && (
              <div data-testid={`signal-reciprocity-benefits-${signal.id}`}>
                <div className="text-[10px] uppercase tracking-wider text-ndor-ink-muted mb-1">
                  Why this may suit you
                </div>
                <ul className="list-disc list-inside text-[11px] text-ndor-ink-secondary space-y-0.5">
                  {signal.reciprocity.candidate_benefit_points.map((p, i) => (
                    <li key={i}>{p}</li>
                  ))}
                </ul>
              </div>
            )}
            {signal.reciprocity?.fit_cautions?.length > 0 && expanded && (
              <div data-testid={`signal-reciprocity-cautions-${signal.id}`}>
                <div className="text-[10px] uppercase tracking-wider text-ndor-ink-muted mb-1">
                  Points to consider
                </div>
                <ul className="list-disc list-inside text-[11px] text-ndor-ink-secondary space-y-0.5">
                  {signal.reciprocity.fit_cautions.map((p, i) => (
                    <li key={i}>{p}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Why they flagged you — secondary on accepted/terminal */}
        {signal.ndor_reasoning_summary && expanded && (
          <div
            className="rounded-md border border-ndor-edge bg-ndor-surface-alt px-3 py-2"
            data-testid={`signal-reasoning-${signal.id}`}
          >
            <div className="text-[10px] uppercase tracking-wider text-ndor-ink-muted mb-1">
              How your profile matched
            </div>
            <p className="text-xs text-ndor-ink leading-relaxed">
              {signal.ndor_reasoning_summary}
            </p>
          </div>
        )}

        {/* Signal body — secondary on accepted/terminal */}
        {signal.body && expanded && (
          <div data-testid={`signal-body-${signal.id}`}>
            <div className="text-[10px] uppercase tracking-wider text-ndor-ink-muted mb-1">
              Their note
            </div>
            <p className="text-sm text-ndor-ink leading-relaxed whitespace-pre-wrap">
              {signal.body}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="pt-2 border-t border-ndor-edge flex items-center justify-end gap-2">
          {isPending ? (
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onRespond(signal.id, 'defer')}
                disabled={pending}
                data-testid={`signal-defer-${signal.id}`}
                title="Come back to this later."
              >
                <PauseCircle className="h-4 w-4 mr-1.5" />
                {pendingAction === 'defer' ? 'Deferring…' : 'Defer'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onRespond(signal.id, 'decline')}
                disabled={pending}
                data-testid={`signal-decline-${signal.id}`}
                style={{
                  borderColor: 'hsl(var(--ndor-state-declined-border))',
                  color: 'hsl(var(--ndor-state-declined-text))',
                }}
              >
                <X className="h-4 w-4 mr-1.5" />
                {pendingAction === 'decline' ? 'Declining…' : 'Decline'}
              </Button>
              <Button
                size="sm"
                onClick={() => onRespond(signal.id, 'accept')}
                disabled={pending}
                data-testid={`signal-accept-${signal.id}`}
                className="bg-ndor-ink text-ndor-surface hover:bg-ndor-ink-muted"
              >
                {pending && pendingAction === 'accept'
                  ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  : <Check className="h-4 w-4 mr-1.5" />}
                {pendingAction === 'accept' ? 'Accepting…' : 'Accept'}
              </Button>
            </>
          ) : (
            <span
              className="text-xs text-ndor-ink-muted"
              data-testid={`signal-responded-${signal.id}`}
            >
              Responded {formatRelativeTime(signal.responded_at)}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}


// ─────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────

function CountTile({ label, value, tone, testId }) {
  const toneClass = {
    emerald: 'text-emerald-200',
    amber:   'text-amber-200',
    rose:    'text-rose-200',
    sky:     'text-sky-900',
  }[tone] || 'text-ndor-ink';
  return (
    <div
      className="rounded-lg border border-ndor-edge bg-ndor-surface-alt px-4 py-3"
      data-testid={testId}
    >
      <div className={`text-xl font-mono tabular-nums ${toneClass}`}>
        {value ?? 0}
      </div>
      <div className="text-[11px] uppercase tracking-wider text-ndor-ink-muted mt-0.5">
        {label}
      </div>
    </div>
  );
}

export default function CandidateSignalsPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [payload, setPayload] = useState(null);
  // Track both the signal being responded to and which action is
  // in flight so the button can show Accepting… / Declining… /
  // Deferring… while the request is active.
  const [responding, setResponding] = useState(null); // { id, action }

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch('/api/candidate/signals');
      setPayload(data || null);
    } catch (err) {
      setError(err?.data?.detail || err?.message || 'Failed to load signals.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onRespond = async (signalId, action) => {
    if (responding) return;
    // Capture previous state so we can restore on failure.
    const prevPayload = payload;
    setResponding({ id: signalId, action });
    try {
      const resp = await apiFetch(
        `/api/candidate/signals/${signalId}/respond`,
        {
          method: 'POST',
          body: JSON.stringify({ action }),
        },
      );
      // Splice the returned signal into payload without a full reload.
      setPayload((prev) => {
        if (!prev) return prev;
        const next = { ...prev, signals: prev.signals.map((s) => (
          s.id === signalId ? resp.signal : s
        )) };
        // Recompute counts — cheap enough at this scale.
        const counts = { pending: 0, accepted: 0, declined: 0, deferred: 0 };
        for (const s of next.signals) {
          if (s.status in counts) counts[s.status] += 1;
        }
        counts.total = next.signals.length;
        next.counts = counts;
        return next;
      });
      if (resp.already_responded) {
        toast.info('You had already responded to this signal.');
      } else if (action === 'accept') {
        toast.success('Accepted. Your profile is now accessible for this role only.');
      } else if (action === 'decline') {
        toast.success('Declined.');
      } else {
        toast.success('Deferred — you can respond later.');
      }
    } catch (err) {
      // Restore previous UI state on failure.
      setPayload(prevPayload);
      toast.error(err?.data?.detail || err?.message || 'Could not respond.');
    } finally {
      setResponding(null);
    }
  };

  const signals = payload?.signals || [];
  const counts  = payload?.counts || { pending: 0, accepted: 0, declined: 0, deferred: 0, total: 0 };

  const sorted = useMemo(() => {
    // Pending first, then by sent_at desc.
    return [...signals].sort((a, b) => {
      if (a.status === 'pending' && b.status !== 'pending') return -1;
      if (a.status !== 'pending' && b.status === 'pending') return 1;
      return new Date(b.sent_at) - new Date(a.sent_at);
    });
  }, [signals]);

  if (loading) {
    return (
      <div
        className="flex items-center justify-center py-24"
        data-testid="candidate-signals-loading"
      >
        <Loader2 className="h-5 w-5 animate-spin text-ndor-ink-muted" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="mx-auto max-w-xl py-24 px-4 text-center"
        data-testid="candidate-signals-error"
      >
        <AlertCircle className="mx-auto mb-4 h-10 w-10 text-rose-500" />
        <p className="text-sm text-ndor-ink-muted">{error}</p>
      </div>
    );
  }

  return (
    <div
      className="mx-auto max-w-3xl py-8 px-4 space-y-6"
      data-testid="candidate-signals-page"
    >
      {/* RC 1.62 Channel-2 lifecycle UX (2026-05-29) — the prior
          "Coming Soon" banner was operator-locked back in RC 1.49.x
          / RC 1.54 when the candidate inbox was still half-built.
          Channel-2 is now LIVE for at least one workspace (the
          operator-fixture account itself holds 4 accepted, 3
          declined, 1 deferred terminal signals). A "Coming Soon"
          banner above a working inbox is doctrinally dishonest —
          dropped per operator audit. The list below is authoritative;
          for users without any signals, the `candidate-signals-empty`
          card already explains the surface. */}

      <div>
        <h1 className="text-2xl font-semibold text-ndor-ink flex items-center gap-2">
          <Inbox className="h-5 w-5 text-ndor-ink-muted" />
          Interest signals
        </h1>
        <p className="mt-1 text-sm text-ndor-ink-muted">
          You decide how to respond to every signal. Your identity
          remains private until you choose otherwise.
        </p>
      </div>

      {counts.total > 0 && (
        <div
          className="grid grid-cols-4 gap-3"
          data-testid="candidate-signals-counts"
        >
          <CountTile label="Pending"   value={counts.pending}  tone="amber"   testId="count-pending" />
          <CountTile label="Accepted"  value={counts.accepted} tone="emerald" testId="count-accepted" />
          <CountTile label="Declined"  value={counts.declined} tone="rose"    testId="count-declined" />
          <CountTile label="Deferred"  value={counts.deferred} tone="sky"     testId="count-deferred" />
        </div>
      )}

      {sorted.length === 0 ? (
        <Card
          className="border-ndor-edge shadow-sm"
          data-testid="candidate-signals-empty"
        >
          <CardContent className="py-14 text-center">
            <Inbox className="mx-auto mb-3 h-8 w-8 text-ndor-ink-muted" />
            <p className="text-sm font-medium text-ndor-ink">
              No signals yet
            </p>
            <p className="text-xs text-ndor-ink-muted mt-1 max-w-md mx-auto">
              When an organisation expresses interest in your profile,
              their signal will appear here. You stay anonymous until
              you decide to accept.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="candidate-signals-list">
          {sorted.map((s) => (
            <SignalCard
              key={s.id}
              signal={s}
              pending={responding?.id === s.id}
              pendingAction={responding?.id === s.id ? responding.action : null}
              onRespond={onRespond}
            />
          ))}
        </div>
      )}

      <p
        className="text-[11px] text-ndor-ink-muted text-center pt-4"
        data-testid="candidate-signals-privacy-note"
      >
        When you accept a signal, your approved profile fields are
        shared with the organisation for this role only. No contact
        details are included.
      </p>
    </div>
  );
}
